import { Button, Dropdown, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { doDeleteBilling, doGetBillingsList } from '../../api/billings';
import { BillingI, BillingSearchParamsT } from '../../interfaces/billing';
import DangerButton from '../../shared/components/buttons/DangerButton';
import PrimaryButton from '../../shared/components/buttons/PrimaryButton';
import BillingsFilterForm from '../../shared/components/forms/billing/Filter';
import BillingsTable from '../../shared/components/tables/Billings';
import { PAGE_SIZE } from '../../shared/constants';
import AddCircleOutlineIcon from '../../shared/svgs/AddCircleOutlineIcon';
import { CurrentUserI } from '../../interfaces/auth';
import { handleAccessControl, handleFilterParams } from '../../utils/global';

const BillingsPage: React.FC = () => {
  const currentUser: CurrentUserI = useOutletContext();
  const access = handleAccessControl(currentUser.roles, 'billings');
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [sortingListState, setSortingListState] = useState<any>();
  const urlSearch = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const queryParams = useMemo(() => {
    const start = Number(urlSearch.get('start')) ?? 0;
    const count = Number(urlSearch.get('count')) !== 0 ? Number(urlSearch.get('count')) : PAGE_SIZE;
    const search = urlSearch.get('filter[title]');
    const orderBy = urlSearch.get('orderBy') ?? urlSearch.get('orderBy')?.split(',');
    const orderType = urlSearch.get('orderType') ?? urlSearch.get('orderType')?.split(',');

    return {
      start,
      count,
      filter: {
        title: search,
      },
      orderBy,
      orderType,
    };
  }, [urlSearch]) as BillingSearchParamsT;
  const { data, isLoading }: any = useQuery(['billings', 'list', queryParams], () => doGetBillingsList(queryParams));

  const currentPage = queryParams.start ? Math.round((Number(queryParams.start) - 1) / PAGE_SIZE + 1) : 1;

  const pagination: any = {
    current: currentPage,
    pageSize: PAGE_SIZE,
    total: data?.total || 0,
    showTotal: (total: string) => `${total} Items`,
  };

  const setQueryParams = (params: any) => {
    const query = handleFilterParams(params);
    navigate(`?${query}`);
  };

  function handleGetSortingList() {
    const orderFields = queryParams.orderBy ? queryParams.orderBy.split(',') : [];
    const orderTypes = queryParams.orderType ? queryParams.orderType.split(',') : [];
    const sortingList: any = [];
    orderFields.forEach((field: string, index: number) => {
      sortingList.push({ [field]: orderTypes[index] });
    });
    setSortingListState(sortingList);
  }

  useEffect(() => {
    handleGetSortingList();
  }, []);

  const handleFilter = (params: any) => {
    const currentPage = params.page;
    const offset = currentPage ? (currentPage === 1 ? 0 : (Number(currentPage) - 1) * PAGE_SIZE) : 0;
    const newQueryParams: any = { ...queryParams, start: offset, count: params.per_page };
    let sortingList: any = [];
    Object.keys(params).forEach((key) => {
      if (key.startsWith('sorts[')) {
        const fieldName = key.replace('sorts[', '').replace(']', '');
        const sortType = params[key].toUpperCase();
        if (fieldName === 'film') {
          const existingFilmIndex = sortingListState?.findIndex((item: any) => item.film !== undefined);
          if (existingFilmIndex !== -1 && existingFilmIndex !== undefined) {
            sortingList = [...sortingListState];
            sortingList[existingFilmIndex] = { film: sortType };
          } else {
            sortingList = [{ film: sortType }, ...sortingListState];
          }
        } else {
          const existingBillDateIndex = sortingListState?.findIndex((item: any) => item.billDate !== undefined);
          if (existingBillDateIndex !== -1 && existingBillDateIndex !== undefined) {
            sortingList = [...sortingListState];
            sortingList[existingBillDateIndex] = { billDate: sortType };
          } else {
            sortingList = [{ billDate: sortType }, ...sortingListState];
          }
        }
        newQueryParams.orderBy = sortingList.map((item: any) => Object.keys(item)[0]).join(',');
        newQueryParams.orderType = sortingList.map((item: any) => Object.values(item)[0]).join(',');
      }
    });

    setQueryParams(newQueryParams);
  };

  const { mutateAsync: mutationDelete } = useMutation(doDeleteBilling, {
    onSuccess: async () => {
      message.info('Deleted!');
      queryClient.invalidateQueries({
        queryKey: ['billings', 'list'],
      });
    },
    onSettled: () => {
      queryClient.prefetchQuery({
        queryKey: ['billings', 'list'],
        queryFn: () => doGetBillingsList({ start: 0, count: PAGE_SIZE, filter: undefined }),
      });
    },
  });

  const handleClickDelete = (billing: BillingI) => {
    mutationDelete(billing.id);
  };

  const handleFilterSubmit = (values: any) => {
    setQueryParams({ ...queryParams, filter: { title: values.title } });
  };

  const handleResetFilter = () => {
    setQueryParams({ start: 0, count: PAGE_SIZE, filter: undefined });
  };

  const withAction = (record: any) => {
    return (
      <span className="action" style={{ display: 'flex' }}>
        <Dropdown.Button
          overlayClassName="table-action-menu"
          menu={{
            items: [
              access.isCanRead
                ? {
                    key: '1',
                    label: (
                      <Link to={`/billings/${record?.id}`}>
                        <Button className="btn-table-action-primary" type="link">
                          View
                        </Button>
                      </Link>
                    ),
                  }
                : null,
              access.isCanUpdate
                ? {
                    key: '2',
                    label: (
                      <Link to={`/billings/${record?.id}/edit`}>
                        <Button className="btn-table-action-primary" type="link">
                          Edit
                        </Button>
                      </Link>
                    ),
                  }
                : null,
              access.isCanDelete
                ? {
                    key: '3',
                    label: <DangerButton label="Delete" onOk={() => handleClickDelete(record)} />,
                  }
                : null,
            ],
          }}
        ></Dropdown.Button>
      </span>
    );
  };

  return (
    <div className="billings-container">
      <div className="page-container">
        <div className="page-header">
          <div></div>
          <div className="page-header-actions">
            {access.isCanCreate && (
              <Link to="/billings/new">
                <PrimaryButton label="Create Billing" icon={<AddCircleOutlineIcon />} />
              </Link>
            )}
          </div>
        </div>
        <div className="page-content">
          <BillingsFilterForm
            handleFilter={handleFilterSubmit}
            handleReset={handleResetFilter}
            queryParams={queryParams}
          />
          <BillingsTable
            rowKey="id"
            loading={isLoading}
            pagination={pagination}
            dataSource={data?.data}
            withAction={withAction}
            onChange2={handleFilter}
            query={queryParams}
          />
        </div>
      </div>
    </div>
  );
};

export default BillingsPage;
